import React from "react"

import { localizer } from '../../../../Translator'

import Grid from '@material-ui/core/Grid'
import Container from '@material-ui/core/Container'
import Button from '@material-ui/core/Button'
import { Backdrop, CircularProgress } from "@material-ui/core"
import Typography from '@material-ui/core/Typography'
import Alert from '@material-ui/lab/Alert'

import { SnackbarCustom } from "../../../Utilities/SnackbarCustom";

import ScreenHeading from '../../../common/ScreenHeading';

import DraftSelector from './DraftSelector'

import OutboxCli from "../../../../sdk/OutboxCli"
import AuthCli from "../../../../sdk/AuthCli.js"

const MAX_FILE_SIZE = 20971520
const MAX_DRAFT_TOTAL_PAGES = 200

/**
 * Panel for drafts selection for a shipment.
 */
export class OutboxDraft extends React.Component {

  constructor(props) {
    super(props)
    this.state = {
      // Represents global shipment list given by parent component to be updated
      shipments: [],

      // Session of the current user. It needs the role of the user for some operation related with the documents
      session: null,

      // Draft list that have to be displayed.
      drafts: [],

      // Number of pages of drafts pagination are currently displayed.
      pagesLoaded: 0,

      // Marks where there are no more drafts to be displayed.
      endOfDrafts: false,

      // Success or error message on failure trying to load drafts.
      draftError: null,

      // Success or error message on failure trying to upload a draft.
      uploadDraftError: null,
      errormessage: null,

      // Waiting for server response indicator.
      isLoading: false
    }
  }

  /**
   * Calling API to get available drafts before component mount
   */
  componentDidMount() {
    this.setState({ shipments: this.props.shipments }, () => {
      let shipment = this.state.shipments && this.state.shipments.length > 0 ? this.state.shipments[0] : null
      if (!shipment) return
       
      if (this.props.draftsSelected) this.setState({ drafts: this.props.draftsSelected })
    })
    
    AuthCli.GetSessionInfo().then(session => {
      if (session) {
        this.setState({ session: session })
      }
    })
  }

  componentDidUpdate(prevProps) {
    if (prevProps === this.props) return
    this.setState({ shipments: this.props.shipments }, () => {
      let shipment = this.state.shipments && this.state.shipments.length > 0 ? this.state.shipments[0] : null
      if (!shipment) return
       
      if (this.props.draftsSelected) this.setState({ drafts: this.props.draftsSelected })
    })
  }

  isBackendErrorCode = (message) => {
    if (message === null || message === undefined) return false
    return /\[{0,1}ERR_[A-Z0-9_]+\}{0,1}\]/.test(message.toString())
  }

  cleanBackendErrorCode = (message) => {
    if (message === null || message === undefined) return localizer.CommsError
    const sanitized = message
      .toString()
      .replace(/\[{0,1}ERR_[A-Z0-9_]+\}{0,1}\]/g, "")
      .trim()

    return sanitized || localizer.CommsError
  }


  /**
   * Send component data to update parent component state
   */
  handleStep = (event) => {
    let direction = event.currentTarget.name
    this.state.shipments.forEach(s => {
      let ids = s.pages.map(p => { return p.draftId })
      this.state.drafts.forEach(d => {
        if (!ids.includes(d.draftId)) {
          s.pages.push({ draftId: d.draftId, pageFirst: 1, pageLast: d.pages })
        }
      })
      
      return s
    })
    
    this.props.handleOutboxState(this.state.shipments, this.state.drafts, 2, direction, null)
  }

  /**
   * Handle onChange event to upload a draft/s. 
   * The user has just selected some files from local drive,
   * these files are uploaded into the outbox service to become new drafts.
   * The panel with available drafts is refreshed with these new drafts.
   */
  handleOnChangeImportDraft = (event) => {
    var files = event.target.files
    if (!files || files.length === 0) return

    //Validates the files before uploading it.
    let errors = this.validateFiles(files)
    if (errors.length > 0) {
      let errormessage = ""
      errors.forEach(error => {errormessage += error + "\n"})
      this.setState({ errormessage: errormessage })
      return
    }
    
    this.setState({ isLoading: true })

    const formData = new FormData();
    for (const file of files) {
      formData.append('files', file);
    }

    OutboxCli.UploadDrafts(formData).then(response => {
      if (response !== null && Array.isArray(response)) {
        const successfulDrafts = response.filter(draft => draft && draft.draftId !== null)
        const failedDrafts = response.filter(draft => draft && draft.draftId === null && this.isBackendErrorCode(draft.description))
        const overLimitDrafts = []
        const acceptedDrafts = []
        const overLimitMessage = localizer.formatString(
          localizer.DraftTotalPagesLimitExceeded || "The total number of selected draft pages cannot exceed {0}.",
          MAX_DRAFT_TOTAL_PAGES
        )

        let currentTotalPages = this.getTotalDraftPages(this.state.drafts)
        for (let index = 0; index < successfulDrafts.length; index++) {
          const draft = successfulDrafts[index]
          const draftPages = Number(draft.pages) || 0
          if (currentTotalPages + draftPages > MAX_DRAFT_TOTAL_PAGES) {
            overLimitDrafts.push(draft)
          } else {
            acceptedDrafts.push(draft)
            currentTotalPages += draftPages
          }
        }

        let error = ""
        for (let index = 0; index < failedDrafts.length; index++) {
          const draft = failedDrafts[index]
          error += draft.documentName + ' - ' + this.cleanBackendErrorCode(draft.description) + '\n'
        }

        for (let index = 0; index < overLimitDrafts.length; index++) {
          const draft = overLimitDrafts[index]
          error += draft.documentName + ' - ' + overLimitMessage + '\n'
        }

        if (acceptedDrafts.length > 0) {
          let drafts = [...this.state.drafts]
          drafts = drafts.concat(acceptedDrafts)
          this.setState({ drafts: drafts, uploadDraftError: error || null })
        } else {
          this.setState({ uploadDraftError: error || null })
        }
      }
      this.setState({ isLoading: false })
    })
  }

  // Validates the files before uploading it.
  validateFiles = (files) => {
    let errors = []
    let totalSize = 0
    for (let i = 0; i < files.length; i++) {
      const file = files[i]
      if (!file) errors.push("Filename: " + file.name + " => " + localizer.FileNotValid)
      totalSize += file.size

      const name = file.name
      const lastDot = name.lastIndexOf('.');
      const ext = name.substring(lastDot + 1);

      if (ext.toLowerCase() !== "pdf") errors.push("Filename: " + file.name + " => " + localizer.FileNotValidExtension)
    }
    if (totalSize > MAX_FILE_SIZE) errors.push(localizer.FileNotValidSize)
    
    return errors
  }

  setShipmentPages = (drafts) => {
    let shipmentpages = []
    drafts.forEach(d => {
      shipmentpages.push({ draftId: d.draftId, pageFirst: 1, pageLast: d.pages })
    })
    return shipmentpages
  }

  getTotalDraftPages = (drafts) => {
    if (!drafts || drafts.length === 0) return 0
    return drafts.reduce((total, draft) => total + (Number(draft && draft.pages) || 0), 0)
  }
  
  /**
   * Extracts a draft selector from the panel containing the available drafts.
   */
  removeDocument = (draft) => {
    this.setState({ isLoading: true })
    OutboxCli.RemoveDraft(draft.draftId).then(data => {
      if (data !== null && this.isBackendErrorCode(data)) {
        this.setState({ errormessage: this.cleanBackendErrorCode(data) })
      } else {
        let drafts = this.state.drafts.filter(d => d.draftId !== draft.draftId)

        //If the draft is deleted, it will clean shipments that have been previously created
        let shipments = this.state.shipments.slice(0, 1).map((shipment, index) => {
          let shipmentaux = {
            ...shipment, features: { ...shipment.features, reference: "", price: 100 },
            destinationAddress: {
              who: "",
              contact: "",
              phone: "",
              identityCard: "",
              countryIso: "",
              locality: "",
              postalCode: "",
              streetType: "",
              streetName: "",
              streetNumber: "",
              streetComplements: "",
              envelopeBox: []
            },
            pages: [],
          }
          return shipmentaux
        })
        
        this.setState({ shipments: shipments, drafts: drafts })

      }
    })
    this.setState({ isLoading: false })
  }

  handleonClose = () => {
    this.setState({ draftError: null, uploadDraftError: null, errormessage: null })
  }
  
  render() {
    let disabledStepNext = (this.state.drafts && this.state.drafts.length > 0) ? false : true
    return (
      <React.Fragment>
        <Container className="pt-20">
          {/* Screen heading. */}
          <ScreenHeading className="typography-generic" icon={<i className="fa-solid fa-file-import icon-button-header mr-20 ml-20"></i>} title={localizer.DraftSelectionText} />
          <input
            accept="application/pdf,.doc,.docx"
            id="contained-button-file"
            multiple
            type="file"
            style={{ display: "none" }}
            disabled={this.props.notNewShipment}
            onChange={this.handleOnChangeImportDraft}
          />
          <label htmlFor="contained-button-file" style={{ width: "100%", float: "right" }}>
            <Button disabled={this.props.notNewShipment} className="button-generic secondary-header mt-20 float-right" variant="contained" component="span" startIcon={<i className="fa-solid fa-plus"></i>}>
              {localizer.Import}
            </Button>
          </label>

          <Grid item md={12}>
            <Alert severity="info" >
              <Typography className="typography-generic color-info" variant="caption" display="inline" gutterBottom>{localizer.DraftSelectionHeaderText}</Typography>
            </Alert>
          </Grid>


          <Grid container direction="row" justify="flex-start" className="container-generic">
            {/* Panel of available drafts */}
            <Grid container item md={12} spacing={2} direction="row" justify="flex-start">
              {this.state.drafts && this.state.drafts.length > 0 ?
                this.state.drafts.map((draft) => {
                  return (
                    <Grid item md={4} key={draft.draftId}>
                      <DraftSelector draft={draft} removeDocument={this.removeDocument} notNewShipment={this.props.notNewShipment} /* handleSelectDraft={this.handleSelectDraft} */></DraftSelector>
                    </Grid>)
                })
                :
                <Typography className="typography-generic m-center">{localizer.DraftNoDocumment}</Typography>
              }
            </Grid>
          </Grid>
          {/* Step buttons */}
          <Grid container direction="row" className="pt-20">
            <Grid item md={6}>
              <Button className="button-generic secondary" variant="contained" name="previous" onClick={this.handleStep}>{localizer.Previous}</Button>
            </Grid>
            <Grid item md={6}>
              <Button className="button-generic primary" variant="contained" disabled={disabledStepNext} name="next" onClick={this.handleStep}>{localizer.Next}</Button>
             
            </Grid>
          </Grid>
        </Container>

        {/* Download draft error. */}
        <SnackbarCustom message={this.state.errormessage} severity="error" handleonClose={this.handleonClose} />
        
        {/* Upload draft error. */}
        <SnackbarCustom message={this.state.uploadDraftError} severity="error" handleonClose={this.handleonClose} />
       
        {/* Waiting for service response. */}
        {this.state.isLoading &&
          <Backdrop open={this.state.isLoading} style={{ zIndex: 100 }}>
            <CircularProgress color="secondary" />
          </Backdrop>
        }
      </React.Fragment>
    )
  }
}


// WEBPACK FOOTER //
// ./src/components/shipments/sections/draft/OutboxDraft.js